/**
 * 词汇系统错误处理和监控
 * 提供统一的错误处理、降级策略和性能监控
 */

interface ErrorLogEntry {
  timestamp: number;
  type: 'vocabulary_check' | 'batch_processing' | 'cache_operation' | 'quiz_controller' | 'preloader';
  error: Error;
  context: Record<string, any>;
  severity: 'low' | 'medium' | 'high' | 'critical';
  resolved?: boolean;
}

interface PerformanceMetrics {
  vocabularyCheckTime: number[];
  batchProcessingTime: number[];
  cacheHitRate: number;
  quizTriggerLatency: number[];
  preloadSuccessRate: number;
  averageResponseTime: number;
}

class VocabularyErrorHandler {
  private errorLog: ErrorLogEntry[] = [];
  private performanceMetrics: PerformanceMetrics = {
    vocabularyCheckTime: [],
    batchProcessingTime: [],
    cacheHitRate: 0,
    quizTriggerLatency: [],
    preloadSuccessRate: 0,
    averageResponseTime: 0
  };
  
  private maxLogSize = 100;
  private circuitBreaker = {
    vocabulary: { failures: 0, lastFailure: 0, isOpen: false },
    batch: { failures: 0, lastFailure: 0, isOpen: false },
    cache: { failures: 0, lastFailure: 0, isOpen: false }
  };
  
  private readonly CIRCUIT_BREAKER_THRESHOLD = 5;
  private readonly CIRCUIT_BREAKER_RESET_TIME = 60000; // 1分钟

  // 记录错误
  logError(
    type: ErrorLogEntry['type'], 
    error: Error, 
    context: Record<string, any> = {}, 
    severity: ErrorLogEntry['severity'] = 'medium'
  ): void {
    const entry: ErrorLogEntry = {
      timestamp: Date.now(),
      type,
      error,
      context,
      severity
    };

    this.errorLog.push(entry);
    
    // 保持日志大小限制
    if (this.errorLog.length > this.maxLogSize) {
      this.errorLog = this.errorLog.slice(-this.maxLogSize);
    }

    // 更新熔断器状态
    this.updateCircuitBreaker(type, true);

    // 根据严重程度处理
    if (severity === 'critical') {
      console.error(`[VocabularySystem] Critical error in ${type}:`, error, context);
    } else if (severity === 'high') {
      console.warn(`[VocabularySystem] High severity error in ${type}:`, error.message);
    } else {
      console.debug(`[VocabularySystem] ${type} error:`, error.message);
    }
  }

  // 记录性能指标
  recordPerformance(type: keyof PerformanceMetrics, value: number): void {
    const metrics = this.performanceMetrics[type];
    
    if (Array.isArray(metrics)) {
      metrics.push(value);
      // 保持数组大小限制
      if (metrics.length > 50) {
        metrics.splice(0, metrics.length - 50);
      }
    } else {
      (this.performanceMetrics as any)[type] = value;
    }
  }

  // 获取平均性能指标
  getAverageMetric(type: keyof PerformanceMetrics): number {
    const metrics = this.performanceMetrics[type];
    
    if (Array.isArray(metrics) && metrics.length > 0) {
      return metrics.reduce((sum, val) => sum + val, 0) / metrics.length;
    }
    
    return typeof metrics === 'number' ? metrics : 0;
  }

  // 熔断器检查
  isCircuitOpen(type: 'vocabulary' | 'batch' | 'cache'): boolean {
    const breaker = this.circuitBreaker[type];
    
    if (breaker.isOpen) {
      // 检查是否可以重置
      if (Date.now() - breaker.lastFailure > this.CIRCUIT_BREAKER_RESET_TIME) {
        breaker.isOpen = false;
        breaker.failures = 0;
        console.log(`[VocabularySystem] Circuit breaker reset for ${type}`);
      }
    }
    
    return breaker.isOpen;
  }

  // 更新熔断器状态
  private updateCircuitBreaker(type: ErrorLogEntry['type'], isFailure: boolean): void {
    const mappedType = this.mapErrorTypeToCircuit(type);
    if (!mappedType) return;

    const breaker = this.circuitBreaker[mappedType];
    
    if (isFailure) {
      breaker.failures++;
      breaker.lastFailure = Date.now();
      
      if (breaker.failures >= this.CIRCUIT_BREAKER_THRESHOLD) {
        breaker.isOpen = true;
        console.warn(`[VocabularySystem] Circuit breaker opened for ${mappedType} after ${breaker.failures} failures`);
      }
    } else {
      // 成功操作重置失败计数
      breaker.failures = Math.max(0, breaker.failures - 1);
    }
  }

  // 映射错误类型到熔断器类型
  private mapErrorTypeToCircuit(type: ErrorLogEntry['type']): 'vocabulary' | 'batch' | 'cache' | null {
    switch (type) {
      case 'vocabulary_check':
        return 'vocabulary';
      case 'batch_processing':
        return 'batch';
      case 'cache_operation':
        return 'cache';
      default:
        return null;
    }
  }

  // 安全执行函数（带错误处理）
  async safeExecute<T>(
    operation: () => Promise<T> | T,
    fallback: T,
    type: ErrorLogEntry['type'],
    context: Record<string, any> = {}
  ): Promise<T> {
    const mappedType = this.mapErrorTypeToCircuit(type);
    
    // 检查熔断器
    if (mappedType && this.isCircuitOpen(mappedType)) {
      console.warn(`[VocabularySystem] ${type} operation skipped due to circuit breaker`);
      return fallback;
    }

    const startTime = performance.now();
    
    try {
      const result = await operation();
      
      // 记录成功的性能指标
      const duration = performance.now() - startTime;
      this.recordPerformance('averageResponseTime', duration);
      
      // 重置熔断器失败计数
      if (mappedType) {
        this.updateCircuitBreaker(type, false);
      }
      
      return result;
    } catch (error) {
      const duration = performance.now() - startTime;
      
      this.logError(
        type, 
        error instanceof Error ? error : new Error(String(error)),
        { ...context, duration },
        'medium'
      );
      
      return fallback;
    }
  }

  // 获取错误统计
  getErrorStats(): {
    totalErrors: number;
    errorsByType: Record<string, number>;
    recentErrors: ErrorLogEntry[];
    circuitBreakerStatus: any;
  } {
    const errorsByType: Record<string, number> = {};
    
    this.errorLog.forEach(entry => {
      errorsByType[entry.type] = (errorsByType[entry.type] || 0) + 1;
    });

    return {
      totalErrors: this.errorLog.length,
      errorsByType,
      recentErrors: this.errorLog.slice(-10),
      circuitBreakerStatus: { ...this.circuitBreaker }
    };
  }

  // 获取性能报告
  getPerformanceReport(): {
    averageVocabularyCheckTime: number;
    averageBatchProcessingTime: number;
    cacheHitRate: number;
    averageQuizLatency: number;
    preloadSuccessRate: number;
    overallAverageResponseTime: number;
  } {
    return {
      averageVocabularyCheckTime: this.getAverageMetric('vocabularyCheckTime'),
      averageBatchProcessingTime: this.getAverageMetric('batchProcessingTime'),
      cacheHitRate: this.performanceMetrics.cacheHitRate,
      averageQuizLatency: this.getAverageMetric('quizTriggerLatency'),
      preloadSuccessRate: this.performanceMetrics.preloadSuccessRate,
      overallAverageResponseTime: this.getAverageMetric('averageResponseTime')
    };
  }

  // 健康检查
  getHealthStatus(): {
    status: 'healthy' | 'degraded' | 'critical';
    issues: string[];
    recommendations: string[];
  } {
    const issues: string[] = [];
    const recommendations: string[] = [];
    let status: 'healthy' | 'degraded' | 'critical' = 'healthy';

    // 检查熔断器状态
    Object.entries(this.circuitBreaker).forEach(([key, breaker]) => {
      if (breaker.isOpen) {
        issues.push(`Circuit breaker open for ${key}`);
        recommendations.push(`Check ${key} system connectivity and performance`);
        status = 'critical';
      } else if (breaker.failures > 2) {
        issues.push(`High failure rate in ${key} (${breaker.failures} failures)`);
        recommendations.push(`Monitor ${key} system closely`);
        if (status === 'healthy') status = 'degraded';
      }
    });

    // 检查性能指标
    const avgResponseTime = this.getAverageMetric('averageResponseTime');
    if (avgResponseTime > 1000) {
      issues.push('High average response time');
      recommendations.push('Consider optimizing vocabulary processing pipeline');
      if (status === 'healthy') status = 'degraded';
    }

    // 检查缓存命中率
    if (this.performanceMetrics.cacheHitRate < 0.5) {
      issues.push('Low cache hit rate');
      recommendations.push('Review caching strategy and cache size limits');
      if (status === 'healthy') status = 'degraded';
    }

    // 检查最近错误
    const recentErrors = this.errorLog.filter(
      entry => Date.now() - entry.timestamp < 300000 // 5分钟内
    );
    
    if (recentErrors.length > 10) {
      issues.push('High error rate in recent period');
      recommendations.push('Investigate recent error patterns');
      status = 'critical';
    }

    return { status, issues, recommendations };
  }

  // 清理日志
  clearLogs(): void {
    this.errorLog = [];
    this.performanceMetrics = {
      vocabularyCheckTime: [],
      batchProcessingTime: [],
      cacheHitRate: 0,
      quizTriggerLatency: [],
      preloadSuccessRate: 0,
      averageResponseTime: 0
    };
    
    // 重置熔断器
    Object.values(this.circuitBreaker).forEach(breaker => {
      breaker.failures = 0;
      breaker.isOpen = false;
      breaker.lastFailure = 0;
    });
  }

  // 导出诊断信息
  exportDiagnostics(): {
    timestamp: number;
    errors: ErrorLogEntry[];
    performance: PerformanceMetrics;
    health: any;
    stats: any;
  } {
    return {
      timestamp: Date.now(),
      errors: [...this.errorLog],
      performance: { ...this.performanceMetrics },
      health: this.getHealthStatus(),
      stats: this.getErrorStats()
    };
  }
}

// 导出单例
export const vocabularyErrorHandler = new VocabularyErrorHandler();

// 便用的装饰器函数
export function withErrorHandling<T extends any[], R>(
  fn: (...args: T) => Promise<R> | R,
  fallback: R,
  type: ErrorLogEntry['type'],
  contextFn?: (...args: T) => Record<string, any>
) {
  return async (...args: T): Promise<R> => {
    const context = contextFn ? contextFn(...args) : {};
    return vocabularyErrorHandler.safeExecute(
      () => fn(...args),
      fallback,
      type,
      context
    );
  };
}